import { useEffect, useState } from 'react';

import { type CompanyInfo, getCompanyInfo, isValidSubdomain } from '../config/subdomainConfig';

export interface UseSubdomainReturn {
  subdomain: string | null;
  isLoading: boolean;
  isValidSubdomain: boolean;
  fullHostname: string;
  companyInfo: CompanyInfo;
}

const extractSubdomain = (hostname: string): string | null => {
  // Remove port if present
  const cleanHostname = hostname.split(':')[0];
  const parts = cleanHostname.split('.');
  // Handle localhost specially since it does not have a .com or .ai
  if (cleanHostname.includes('localhost') || cleanHostname.includes('127.0.0.1')) {
    // banco.localhost → 'banco', localhost → null
    return parts.length > 1 ? parts[0] : null;
  }

  // Handle production domains
  const startIdx = parts[0] === 'www' ? 1 : 0;

  // Need at least 3 parts for a subdomain (or 4 with www)
  if (parts.length < 3 + startIdx) return null;

  return parts[startIdx];
};

const useSubdomain = (): UseSubdomainReturn => {
  const [subdomain, setSubdomain] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fullHostname, setFullHostname] = useState('');

  useEffect(() => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      setIsLoading(false);
      return;
    }

    try {
      const { hostname } = window.location;
      setFullHostname(hostname);

      // Extract subdomain
      const extractedSubdomain = extractSubdomain(hostname);
      setSubdomain(extractedSubdomain);
    } catch (_error) {
      // Error extracting subdomain
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get company info based on subdomain
  const companyInfo = getCompanyInfo(subdomain);
  const validSubdomain = subdomain ? isValidSubdomain(subdomain) : false;
  return {
    subdomain,
    isLoading,
    isValidSubdomain: validSubdomain,
    fullHostname,
    companyInfo,
  };
};

export default useSubdomain;
